import { ReactElement } from 'react';
import { useTranslation } from 'next-i18next';

import Button from '@Hotels-at-Home/hah-fe-core/dist/components/Button';
import TitleFlip from '../../helpers/TitleFlip';
import { sanitizeHtml } from '@Hotels-at-Home/hah-fe-core/dist/utils';

const LinensSection = (): ReactElement => {
  const { t } = useTranslation('', { keyPrefix: '$pages.$home.$linensSection' });
  const { t: tButton } = useTranslation('', { keyPrefix: '$common.$buttons' });
  const { t: tCommon } = useTranslation('', { keyPrefix: '$common' });
  const { t: tLink } = useTranslation('', { keyPrefix: '$local.$homePageLinks.$linens' });

  return (
    <section className="base-section linens">
      <div className="desktop-viewport">
        <h2 className="section-title" data-gsap="fade-up" data-gsap-delay="0.6">
          <TitleFlip normalText={t('marriott')}>
            <span className="accent-bar">
              {t('linens')}
              <em data-gsap="grow" data-gsap-delay="0.8" data-gsap-duration="0.8" />
            </span>
          </TitleFlip>
        </h2>
        <a href={tLink('signatureCollection')}>
          <div data-gsap="fade" data-gsap-delay="0.03" className="title">
            <h3 data-gsap="fade-up" data-gsap-delay="0.65">
              {t('marriottSignature')}
            </h3>
            <Button
              data-gsap="fade-right"
              data-gsap-delay="0.65"
              id="btn_HP_linens_sheet-set_shopnow"
              ariaLabel={`${tButton('shopNow')}: ${t('marriottSignature')}`}
              variant="plain"
              noTabbing
            >
              {tButton('shopNow')}
            </Button>
          </div>
        </a>
        <div className="linen-products">
          <div className="linen-product-wrap">
            <div className="branding-hero">
              <a href={tLink('signatureCollection')} id="HP_linens_signature-linens-set">
                <picture>
                  <source srcSet="/images/homePage/fill.gif" media="(max-width: 800px)" />
                  <source srcSet="/images/homePage/linensSection/hp-linens.webp" />
                  <img
                    data-gsap="zoom-out"
                    data-gsap-delay="0.03"
                    src="/images/homePage/linensSection/hp-linens.webp"
                    alt={t('marriottLinens')}
                    loading="lazy"
                    width="759"
                    height="743"
                    decoding="async"
                    fetchPriority="low"
                  />
                </picture>
              </a>
            </div>
          </div>
          <div className="linen-product-wrap">
            <a href={tLink('signatureCollection')} aria-label={t('linens')}>
              <blockquote
                data-gsap="fade-up"
                data-gsap-target="children"
                data-gsap-delay="0.4"
                data-gsap-stagger="0.05"
              >
                <p className="quote">{t('softestHotel')}</p>
                <hr />
                <p className="quoter">{t('goodHousekeeping')}</p>
              </blockquote>
              <div className="linen-details">
                <p>
                  <span
                    data-gsap="fade-down"
                    data-gsap-delay="0.6"
                    className="mar-icon-cotton-blend"
                  />
                  <span data-gsap="fade-up" data-gsap-delay="0.6" className="txt">
                    {t('qualityCotton')}
                  </span>
                </p>
                <p>
                  <span data-gsap="fade-down" data-gsap-delay="0.6" className="mar-icon-iron" />
                  <span data-gsap="fade-up" data-gsap-delay="0.6" className="txt">
                    {t('wrinkle')}
                  </span>
                </p>
                <p>
                  <span
                    data-gsap="fade-down"
                    data-gsap-delay="0.6"
                    className="mar-icon-washing-machine"
                  />
                  <span data-gsap="fade-up" data-gsap-delay="0.6" className="txt">
                    {t('easyCare')}
                  </span>
                </p>
              </div>
            </a>
          </div>

          <div className="linen-product-wrap">
            <div data-gsap="fade" data-gsap-delay="0.03" className="stack">
              <h3
                data-gsap="fade-up"
                data-gsap-delay="0.65"
                className="product-title"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(t('duvetCovers')) }}
              />
              <p data-gsap="fade-up" data-gsap-delay="0.7">
                <Button
                  href={tLink('duvetCovers')}
                  id="btn_HP_linens_duvet-cover_shopnow"
                  variant="plain"
                  ariaLabel={`${tButton('shopNow')}: ${t('duvetCovers')}`}
                >
                  {tButton('shopNow')}
                </Button>
              </p>
            </div>
          </div>

          <div className="linen-product-wrap">
            <a
              href={tLink('duvetCovers')}
              id="HP_linens_duvet-cover"
              className="hover-btn-animation"
            >
              <picture>
                <source srcSet="/images/homePage/fill.gif" media="(max-width: 800px)" />
                <source srcSet="/images/homePage/linensSection/hp-duvet-cover.webp" />
                <img
                  data-gsap="fade"
                  data-gsap-delay="0.4"
                  src="/images/homePage/linensSection/hp-duvet-cover.webp"
                  alt={t('duvetCover')}
                  loading="lazy"
                  width="918"
                  height="474"
                  decoding="async"
                  fetchPriority="low"
                />
              </picture>
              <div className="hover-content">
                <Button noTabbing ariaLabel={`${tButton('shopNow')}: ${t('duvetCover')}`}>
                  {tButton('shopNow')}
                </Button>
              </div>
            </a>
          </div>

          <div className="linen-product-wrap">
            <a
              href={tLink('hemstitchCollection')}
              id="HP_linens_blue-hemstitch-sheets"
              className="hover-btn-animation"
            >
              <picture>
                <source srcSet="/images/homePage/fill.gif" media="(max-width: 800px)" />
                <source srcSet="/images/homePage/linensSection/hp-hemstitch-blue.webp" />
                <img
                  data-gsap="fade"
                  data-gsap-delay="0.4"
                  src="/images/homePage/linensSection/hp-hemstitch-blue.webp"
                  alt={t('blueHemstitch')}
                  loading="lazy"
                  width="814"
                  height="475"
                  decoding="async"
                  fetchPriority="low"
                />
              </picture>
              <div className="hover-content">
                <Button noTabbing ariaLabel={`${tButton('shopNow')}: ${t('blueHemstitch')}`}>
                  {tButton('shopNow')}
                </Button>
              </div>
            </a>
          </div>

          <div data-gsap="fade" data-gsap-delay="0.4" className="linen-product-wrap">
            <a
              href={tLink('hemstitchCollection')}
              id="HP_linens_ivory-hemstitch-sheets"
              className="hover-btn-animation"
              aria-label={t('ivoryHemstitch')}
            >
              <picture>
                <source srcSet="/images/homePage/fill.gif" media="(max-width: 1024px)" />
                <source srcSet="/images/homePage/linensSection/hp-hemstitch-ivory.webp" />
                <img
                  src="/images/homePage/linensSection/hp-hemstitch-ivory.webp"
                  alt={t('ivoryHemstitch')}
                  loading="lazy"
                  width="814"
                  height="475"
                  decoding="async"
                  fetchPriority="low"
                />
              </picture>
              <div className="hover-content">
                <Button noTabbing ariaLabel={`${tButton('shopNow')}: ${t('ivoryHemstitch')}`}>
                  {tButton('shopNow')}
                </Button>
              </div>
            </a>
          </div>

          <div className="linen-product-wrap">
            <a
              href={tLink('hemstitchCollection')}
              id="HP_linens_grey-hemstitch-sheets"
              className="hover-btn-animation"
            >
              <picture>
                <source srcSet="/images/homePage/fill.gif" media="(max-width: 800px)" />
                <source srcSet="/images/homePage/linensSection/hp-hemstitch-grey.webp" />
                <img
                  data-gsap="fade"
                  data-gsap-delay="0.4"
                  src="/images/homePage/linensSection/hp-hemstitch-grey.webp"
                  alt={t('greyHemstitch')}
                  loading="lazy"
                  width="814"
                  height="475"
                  decoding="async"
                  fetchPriority="low"
                />
              </picture>
              <div className="hover-content">
                <Button noTabbing ariaLabel={`${tButton('shopNow')}: ${t('greyHemstitch')}`}>
                  {tButton('shopNow')}
                </Button>
              </div>
            </a>
          </div>

          <div className="linen-product-wrap">
            <div data-gsap="fade" data-gsap-delay="0.03" className="stack">
              <h3 data-gsap="fade-up" data-gsap-delay="0.4" className="product-title">
                {t('hemstitchSheets')}
              </h3>
              <p data-gsap="fade-up" data-gsap-delay="0.45">
                <Button
                  href={tLink('hemstitchCollection')}
                  id="btn_HP_linens_hemstitch-sheets_shopnow"
                  variant="plain"
                  ariaLabel={`${tButton('shopNow')}: ${t('hemstitchSheets')}`}
                >
                  {tButton('shopNow')}
                </Button>
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="hp-mobile-viewport">
        <div className="mob-linens">
          <h2 className="mob-title">
            <TitleFlip normalText={t('marriott')}>
              <span className="accent-bar">
                {t('linens')}
                <em data-gsap="grow" data-gsap-delay="0.06" data-gsap-duration="0.8" />
              </span>
            </TitleFlip>
          </h2>
          <a
            href={tLink('signatureCollection')}
            id="HP_signatureSheets-mob"
            className="product-link"
          >
            <blockquote className="product-blockquote">
              <p className="quote">{t('softestHotel')}</p>
              <hr />
              <p>{t('goodHousekeeping')}</p>
            </blockquote>
            <picture>
              <source srcSet="/images/homePage/fill.gif" media="(min-width: 801px)" />
              <source srcSet="/images/homePage/linensSection/hp-linens-mob.webp" />
              <img
                className="img"
                src="/images/homePage/linensSection/hp-linens-mob.webp"
                alt={tCommon('productName', { productName: t('signatureLinenSet') })}
                loading="lazy"
                width="759"
                height="743"
                decoding="async"
                fetchPriority="low"
              />
            </picture>
            <h3 className="mob-section mob-section-full-width txt">{t('signatureLinenSet')}</h3>
          </a>

          <div className="shop-list">
            <a className="shop-list-item" href={tLink('hemstitchCollection')}>
              <picture>
                <source srcSet="/images/homePage/fill.gif" media="(min-width: 801px)" />
                <source srcSet="/images/homePage/linensSection/Marriott-hem-stitch-sheets_lrg.webp" />
                <img
                  className="img lazy"
                  src="/images/homePage/linensSection/Marriott-hem-stitch-sheets_lrg.webp"
                  alt={tCommon('productName', { productName: t('hemstitchSheets') })}
                  loading="lazy"
                  width="1123"
                  height="732"
                  decoding="async"
                  fetchPriority="low"
                />
              </picture>
              <h3
                className="mob-section mob-section-half-width txt"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(t('hemstitchSheets')) }}
              />
            </a>
            <a className="shop-list-item" href={tLink('duvetCovers')}>
              <picture>
                <source srcSet="/images/homePage/fill.gif" media="(min-width: 801px)" />
                <source srcSet="/images/homePage/linensSection/marriott-signature-duvet-cover-and-shams-set-MAR-101-DS-02-WH_lrg.webp" />
                <img
                  className="img lazy"
                  src="/images/homePage/linensSection/marriott-signature-duvet-cover-and-shams-set-MAR-101-DS-02-WH_lrg.webp"
                  alt={tCommon('productName', { productName: t('duvetCovers') })}
                  loading="lazy"
                  width="1123"
                  height="732"
                  decoding="async"
                  fetchPriority="low"
                />
              </picture>
              <h3
                className="mob-section mob-section-half-width txt"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(t('duvetCovers')) }}
              />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LinensSection;
